import { FC, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CaretDown,
  Dropdown,
  LeaderBoardLabel,
  LeaderBoardRow,
  Typography,
  TabGroup,
  RadioGroup,
  RadioGroupItem,
  InputField,
  Button,
  PageHero,
} from '@dreamteam-monorepo/ui';
import {
  LEAGUE_OVERALL_PERIOD_VALUE,
  LeagueStandingEntry,
  useLeagueDetailStore,
} from '@/stores/LeagueDetailStore';
import { useAuthStore } from '@/stores/AuthStore';
import { useCurrentTournamentCalendar } from '@/hooks/useCurrentTournamentCalendar';
import { useLeaderboardPeriods } from '@/hooks/useLeaderboardPeriods';
import { ContentAdSlot } from '@/ads';
import { BELeague, BELeagueEntry } from '@/lib/leagueMappers';
import { getErrorMessage, recordCompletedLeagueShare } from '@/lib/network';
import type { LeagueHistoryItem } from '@/lib/network';
import { trackFantasyEvent } from '@/analytics/fantasyEventTracking';
import { cn } from '@/lib/utils';
import { LeagueStandingsTable } from './components/LeagueStandingsTable';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatUpdatedAt(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d
      .toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      })
      .toUpperCase();
  } catch {
    return dateStr;
  }
}

function formatDeadline(date: Date): string {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const h = date.getHours();
  const min = String(date.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${dd}/${mm}, ${h12}:${min}${ampm}`;
}

function entryToRow(entry: LeagueStandingEntry): LeaderBoardRow {
  return {
    variant: 'default',
    rank: entry.rank ?? '-',
    teamName: entry.fantasy_team?.team_name ?? '-',
    managerName: entry.user?.username ?? undefined,
    average: entry.average_points != null ? entry.average_points.toFixed(1) : '-',
    gameweekPoints: entry.period_points != null ? String(Math.round(entry.period_points)) : '-',
    total: entry.points,
  };
}

function formatTournamentLabel(item: LeagueHistoryItem): string {
  return item.season_label || 'Unknown season';
}

function getSystemLeagueDescription(league: BELeague): string | null {
  if (league.description) {
    return league.description;
  }

  if (league.type === 'club') {
    return `This is the official ${league.name} club league. Teams are ranked here based on their selected club.`;
  }

  if (league.type === 'national') {
    return `This is the official ${league.name} country league. Teams are ranked here based on their selected country.`;
  }

  return null;
}

// ─── Subcomponents ────────────────────────────────────────────────────────────

const InfoChip: FC<{ label: string; value: string; dark?: boolean }> = ({ label, value, dark }) => (
  <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
    <Typography
      variant="paragraph010"
      className="text-[#222526] text-center truncate w-full text-[10px] sm:text-[14px]"
    >
      {label}
    </Typography>
    <div
      className={[
        'w-full rounded-[--corner-xs] px-2 py-1 flex items-center justify-center',
        dark ? 'bg-[#201d4a]' : 'bg-white',
      ].join(' ')}
    >
      <Typography
        variant="subheadline020"
        className={cn(
          'truncate',
          dark ? 'text-white' : 'text-[#222526]',
          'text-[12px] sm:text-[16px]'
        )}
      >
        {value}
      </Typography>
    </div>
  </div>
);

const InviteLeagueBanner: FC<{ league: BELeague }> = ({ league }) => {
  const inviteLink = `${window.location.origin}/leagues/join/${league.join_code}`;
  const recordShare = (method: 'copy_code' | 'copy_link' | 'native_share') => {
    void recordCompletedLeagueShare(league.id, method).catch(() => {});
  };

  const copyCode = () => {
    trackFantasyEvent('leagueInvite', { link_name: 'Copy League Code' });
    return navigator.clipboard.writeText(league.join_code).then(() => {
      toast.success('League code copied!');
      recordShare('copy_code');
    });
  };

  const copyLink = () => {
    trackFantasyEvent('leagueInvite', { link_name: 'Copy League Link' });
    return navigator.clipboard.writeText(inviteLink).then(() => {
      toast.success('Invite link copied!');
      recordShare('copy_link');
    });
  };

  const share = async () => {
    trackFantasyEvent('leagueInvite', { link_name: 'Share My League' });
    if (navigator.share) {
      try {
        await navigator.share({ title: league.name, url: inviteLink });
        recordShare('native_share');
      } catch {
        /* cancelled */
      }
    } else {
      await navigator.clipboard.writeText(inviteLink).then(() => {
        toast.success('Invite link copied!');
        recordShare('copy_link');
      });
    }
  };

  return (
    <div className="bg-[--banner-bg] rounded-[--corner-md] px-[--padding-md] py-[--padding-lg] flex flex-col gap-[--padding-lg] w-full">
      <div className="flex flex-col gap-2">
        <Typography as="h2" variant="headline030" className="text-[--banner-text] !font-[900]">
          INVITE YOUR FRIENDS
        </Typography>
        <Typography variant="paragraph020" className="text-[--banner-text]">
          You can invite as many people to join your league.
        </Typography>
      </div>
      <div className="flex flex-col sm:flex-row gap-[--padding-sm]">
        <Button variant="outlined" color="blue" onClick={copyCode} className="w-full sm:flex-1">
          Copy League Code
        </Button>
        <Button variant="outlined" color="blue" onClick={copyLink} className="w-full sm:flex-1">
          Copy League Link
        </Button>
        <Button variant="contained" color="blue" onClick={share} className="w-full sm:flex-1">
          Share My League
        </Button>
      </div>
    </div>
  );
};

const RegenerateLeagueBanner: FC<{ onRegenerate: () => void }> = ({ onRegenerate }) => (
  <div className="bg-[--banner-bg] rounded-[--corner-md] px-[--padding-md] py-[--padding-lg] flex flex-col gap-[--padding-lg] w-full">
    <div className="flex flex-col gap-2">
      <Typography as="h2" variant="headline030" className="text-[--banner-text] !font-[900]">
        PLAY AGAIN THIS SEASON
      </Typography>
      <Typography variant="paragraph020" className="text-[--banner-text]">
        This league is from a previous season. Regenerate it to create a fresh league with the same
        name and members, standings start empty.
      </Typography>
    </div>
    <Button
      variant="contained"
      color="blue"
      onClick={onRegenerate}
      className="w-full sm:self-start"
    >
      Regenerate League
    </Button>
  </div>
);

const RegenerateLeagueModal: FC<{
  league: BELeague;
  periodLabel: string;
  periodOptions: { id: string; label: string; value: string }[];
  onConfirm: (startGameweek: number) => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ league, periodLabel, periodOptions, onConfirm, onCancel, loading }) => {
  const [scoringStartsValue, setScoringStartsValue] = useState<string[]>([]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-[--corner-md] p-[--padding-xl] flex flex-col gap-[--padding-lg] max-w-sm w-full">
        <Typography
          as="h2"
          variant="headline030"
          className="text-[--color-neutral-900] !font-[900]"
        >
          REGENERATE LEAGUE
        </Typography>
        <Typography variant="paragraph020" className="text-[--color-neutral-900]">
          This creates a fresh league named <strong>{league.name}</strong> for the new season. The
          original members will be re-added and standings start empty.
        </Typography>
        <Dropdown
          label="Scoring Starts*"
          options={periodOptions}
          values={scoringStartsValue}
          emptyValue={`Select ${periodLabel}`}
          onValueChange={(option, checked) => setScoringStartsValue(checked ? [option.value] : [])}
          completed={scoringStartsValue.length > 0}
          onWhite
        />
        <Typography variant="smallprint010R" className="text-[--color-neutral-600]">
          This will be when scoring starts for your league.
        </Typography>
        <div className="flex gap-[--padding-sm]">
          <Button
            variant="outlined"
            color="main"
            onClick={onCancel}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="blue"
            onClick={() => onConfirm(Number(scoringStartsValue[0]))}
            loading={loading}
            disabled={loading || scoringStartsValue.length === 0}
            className="flex-1"
          >
            Regenerate
          </Button>
        </div>
      </div>
    </div>
  );
};

const ConfirmRemoveModal: FC<{
  entry: BELeagueEntry;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ entry, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
    <div className="bg-white rounded-[--corner-md] p-[--padding-xl] flex flex-col gap-[--padding-lg] max-w-sm w-full">
      <Typography as="h2" variant="headline030" className="text-[--color-neutral-900] !font-[900]">
        REMOVE PLAYER
      </Typography>
      <Typography variant="paragraph020" className="text-[--color-neutral-900]">
        Are you sure you want to remove{' '}
        <strong>{entry.fantasy_team?.team_name ?? entry.user?.username ?? 'this player'}</strong>{' '}
        from the league?
      </Typography>
      <div className="flex gap-[--padding-sm]">
        <Button
          variant="outlined"
          color="main"
          onClick={onCancel}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="red"
          onClick={onConfirm}
          loading={loading}
          disabled={loading}
          className="flex-1"
        >
          Remove
        </Button>
      </div>
    </div>
  </div>
);

// ─── Constants ────────────────────────────────────────────────────────────────

const CHAIR_TABS = [
  { id: 'table', label: 'League Table' },
  { id: 'manage', label: 'Manage League' },
];

const MEMBER_TABS = [
  { id: 'table', label: 'League Table' },
  { id: 'details', label: 'Details' },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

const LeagueDetail: FC = () => {
  const { leagueId = '' } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const justJoined = !!(locationState as { justJoined?: boolean } | null)?.justJoined;
  const sessionState = useAuthStore(s => s.sessionState);
  const userId = useAuthStore(s => s.userId);
  const {
    league,
    history,
    entries,
    myEntry,
    nextDeadline,
    leaderboardPeriodType,
    leaderboardPeriods,
    leaderboardLastUpdated,
    loading,
    updating,
    regenerating,
    removingEntryId,
    fetchLeagueDetail,
    updateLeague,
    regenerateLeague,
    removeEntry,
    reset,
  } = useLeagueDetailStore();

  const [activeTab, setActiveTab] = useState<'table' | 'manage' | 'details'>('table');
  const [selectedPeriodId, setSelectedPeriodId] = useState(LEAGUE_OVERALL_PERIOD_VALUE);
  const [confirmRemove, setConfirmRemove] = useState<BELeagueEntry | null>(null);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPrivacy, setFormPrivacy] = useState<'private' | 'public'>('private');

  const isMiniLeague = league?.type === 'mini_league';
  const isChair = !!(userId && league?.commissioner_user_id === userId);
  const canManageLeague = isChair && isMiniLeague;
  const leagueDescription = league ? getSystemLeagueDescription(league) : null;
  const isAuthenticated = sessionState === 'authenticated';

  // A league from an earlier season can no longer be joined (fantasy teams are
  // per-season), so its invite banner is replaced by the regenerate flow.
  const { data: currentCalendar } = useCurrentTournamentCalendar();
  const isPreviousSeason = !!(
    league &&
    currentCalendar &&
    league.tournament_calendar_id !== currentCalendar.id
  );
  // History is newest first, so any edition other than the head already has a
  // successor and regenerating it again would be rejected by the backend.
  const hasSuccessor = history.length > 0 && history[0].id !== leagueId;
  const canRegenerate = canManageLeague && isPreviousSeason && !hasSuccessor;

  // "Scoring Starts" choices for the regenerated league come from the CURRENT
  // season's calendar (matchdays or gameweeks, upcoming included).
  const { data: currentSeasonPeriods } = useLeaderboardPeriods(
    canRegenerate ? (currentCalendar?.id ?? null) : null,
    true
  );
  const regeneratePeriodLabel = currentSeasonPeriods?.type === 'matchday' ? 'Matchday' : 'Gameweek';
  const regeneratePeriodOptions = (currentSeasonPeriods?.periods ?? []).map(period => ({
    id: String(period.number),
    label: `${regeneratePeriodLabel} ${period.number}`,
    value: String(period.number),
  }));

  useEffect(() => {
    if (!isAuthenticated || !leagueId) return;
    if (selectedPeriodId === LEAGUE_OVERALL_PERIOD_VALUE) {
      fetchLeagueDetail(leagueId);
    } else {
      fetchLeagueDetail(leagueId, selectedPeriodId);
    }
  }, [isAuthenticated, leagueId, selectedPeriodId, fetchLeagueDetail]);

  useEffect(() => {
    return () => reset();
  }, [reset]);

  useEffect(() => {
    setSelectedPeriodId(LEAGUE_OVERALL_PERIOD_VALUE);
  }, [leagueId]);

  useEffect(() => {
    if (league) {
      setFormName(league.name);
      setFormDescription(league.description ?? '');
      setFormPrivacy(league.is_public ? 'public' : 'private');
    }
  }, [league]);

  useEffect(() => {
    if (justJoined) toast.success('You have successfully joined the league!');
  }, [justJoined]);

  const isDirty =
    !!league &&
    (formName !== league.name ||
      formDescription !== (league.description ?? '') ||
      formPrivacy !== (league.is_public ? 'public' : 'private'));

  const lastUpdated = league ? formatUpdatedAt(leaderboardLastUpdated ?? league.updated_at) : '';
  const periodLabel = leaderboardPeriodType === 'matchday' ? 'Matchday' : 'Gameweek';
  const pointsColumnLabel = leaderboardPeriodType === 'matchday' ? 'MD PTS' : 'GW PTS';
  // Overall shows the season view (average + total); a specific
  // gameweek/matchday shows the points scored in that period instead.
  const isOverallSelected = selectedPeriodId === LEAGUE_OVERALL_PERIOD_VALUE;
  const periodOptions = [
    { id: LEAGUE_OVERALL_PERIOD_VALUE, label: 'Overall', value: LEAGUE_OVERALL_PERIOD_VALUE },
    ...leaderboardPeriods.map(period => ({
      id: period.id,
      label: `${periodLabel} ${period.number}`,
      value: period.id,
    })),
  ];

  // Regenerated leagues carry their whole chain (newest first); each option is
  // one tournament edition and switching navigates to that edition's table.
  const tournamentOptions = history.map(item => ({
    id: item.id,
    label: formatTournamentLabel(item),
    value: item.id,
  }));

  const handleUpdateSettings = async () => {
    if (!league || !isDirty) return;
    try {
      await updateLeague(league.id, {
        name: formName.trim(),
        description: formDescription.trim() || undefined,
        is_public: formPrivacy === 'public',
      });
      trackFantasyEvent('leagueSettingsUpdated');
      toast.success('League settings updated!');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not update the league settings. Please try again.'));
    }
  };

  const handleRegenerate = async (startGameweek: number) => {
    if (!league || !Number.isFinite(startGameweek)) return;
    try {
      const newLeagueId = await regenerateLeague(league.id, startGameweek);
      setShowRegenerateModal(false);
      toast.success('League regenerated for the new season!');
      navigate(`/leagues/${newLeagueId}`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not regenerate the league. Please try again.'));
    }
  };

  const handleConfirmRemove = async () => {
    if (!confirmRemove || !league) return;
    try {
      await removeEntry(league.id, confirmRemove.id);
      trackFantasyEvent('leaguePlayerRemoved');
      toast.success('Player removed from league.');
      setConfirmRemove(null);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not remove the player. Please try again.'));
      setConfirmRemove(null);
    }
  };

  return (
    <div className="components light flex flex-col w-full">
      {confirmRemove && (
        <ConfirmRemoveModal
          entry={confirmRemove}
          onConfirm={handleConfirmRemove}
          onCancel={() => setConfirmRemove(null)}
          loading={!!removingEntryId}
        />
      )}

      {showRegenerateModal && league && (
        <RegenerateLeagueModal
          league={league}
          periodLabel={regeneratePeriodLabel}
          periodOptions={regeneratePeriodOptions}
          onConfirm={handleRegenerate}
          onCancel={() => setShowRegenerateModal(false)}
          loading={regenerating}
        />
      )}

      {/* ── Hero banner (lavender) ───────────────────────────────────────── */}
      <PageHero>
        <div className="flex flex-col gap-[--padding-md]">
          <button
            type="button"
            onClick={() => navigate('/leagues')}
            className="flex items-center gap-2 self-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 rounded-[--corner-xs]"
          >
            <ArrowLeft className="size-6 text-[--color-blue-200]" />
            <Typography as="span" variant="button020" className="text-[--color-blue-200]">
              Back
            </Typography>
          </button>

          <Typography
            as="h1"
            variant="headline050"
            className="text-[--page-header-header] !font-[900]"
          >
            {loading ? ' ' : (league?.name ?? 'LEAGUE')}
          </Typography>

          {league && (
            <div className="flex gap-1 sm:gap-[--padding-md] items-start w-full">
              {nextDeadline ? (
                <InfoChip
                  label="Next Deadline:"
                  value={formatDeadline(nextDeadline.deadline)}
                  dark
                />
              ) : (
                <InfoChip label="Next Deadline:" value="N/A" dark />
              )}
              <InfoChip
                label="Scoring Starts:"
                value={`${periodLabel} ${league.start_gameweek || 1}`}
              />
              <InfoChip label="Settings:" value={league.is_public ? 'Public' : 'Private'} />
            </div>
          )}
        </div>
      </PageHero>

      {/* ── White controls bar ──────────────────────────────────────────── */}
      <div className="bg-white w-full border-b border-[#eae9fd]">
        <div className="max-w-[846px] mx-auto w-full px-4 py-4 flex flex-col gap-4">
          <TabGroup
            tabs={canManageLeague ? CHAIR_TABS : MEMBER_TABS}
            selectedId={activeTab}
            onChangeTab={id => {
              const tab = id as 'table' | 'manage' | 'details';
              const tabLabel =
                tab === 'table' ? 'League Table' : tab === 'manage' ? 'Manage League' : 'Details';
              trackFantasyEvent('leagueDataToggle', { toggle_name: tabLabel });
              setActiveTab(tab);
            }}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              {league && (
                <>
                  <Typography as="p" variant="paragraph010" className="text-[--color-neutral-900]">
                    LAST UPDATED:
                  </Typography>
                  <Typography
                    as="p"
                    variant="paragraph010"
                    className="!font-bold text-[--color-neutral-900]"
                  >
                    {lastUpdated}
                  </Typography>
                </>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {tournamentOptions.length > 1 && (
                <Dropdown
                  className="w-[190px] shrink-0"
                  options={tournamentOptions}
                  label=""
                  values={[leagueId]}
                  onValueChange={option => {
                    if (option.value !== leagueId) navigate(`/leagues/${option.value}`);
                  }}
                />
              )}
              <Dropdown
                className="w-[150px] shrink-0"
                options={periodOptions}
                label=""
                values={[selectedPeriodId]}
                onValueChange={option => setSelectedPeriodId(option.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── League Table tab ────────────────────────────────────────────── */}
      {activeTab === 'table' && (
        <div className="max-w-[846px] mx-auto w-full flex flex-col">
          <LeagueStandingsTable
            leagueId={leagueId}
            tournamentCalendarId={league?.tournament_calendar_id ?? null}
            periodType={leaderboardPeriodType}
            selectedPeriodId={selectedPeriodId}
            pointsColumnLabel={pointsColumnLabel}
            leaderboardPeriods={leaderboardPeriods}
          />

          {league && isMiniLeague && !isPreviousSeason && (
            <div className="px-4 py-[--padding-lg]">
              <InviteLeagueBanner league={league} />
            </div>
          )}

          {canRegenerate && (
            <div className="px-4 py-[--padding-lg]">
              <RegenerateLeagueBanner onRegenerate={() => setShowRegenerateModal(true)} />
            </div>
          )}
        </div>
      )}

      {/* ── Manage League tab (chair only) ──────────────────────────────── */}
      {activeTab === 'manage' && canManageLeague && league && (
        <div className="max-w-[846px] mx-auto w-full flex flex-col gap-[--padding-2xl] px-4 py-[--padding-lg]">
          <div className="flex flex-col gap-[--padding-lg]">
            <RadioGroup
              value={formPrivacy}
              onValueChange={v => setFormPrivacy(v as 'private' | 'public')}
              className="flex-row gap-[--padding-xl]"
            >
              <RadioGroupItem value="private" id="manage-privacy-private" label="Private" />
              <RadioGroupItem value="public" id="manage-privacy-public" label="Public" />
            </RadioGroup>

            <div className="flex flex-col sm:flex-row gap-[--padding-xl]">
              <div className="flex-1 min-w-0">
                <InputField
                  id="manage-league-name"
                  fieldsetClassName="w-full"
                  containerClassName="w-full"
                  label="League Name*"
                  placeholder="Maximum 30 characters"
                  value={formName}
                  onChange={e => setFormName(e.target.value.slice(0, 30))}
                  maxCharCount={30}
                  supportText="Inappropiate names will not be accepted."
                  onWhite
                />
              </div>

              <fieldset className="flex flex-col gap-[--padding-xs] flex-1 min-w-0">
                <Typography variant="paragraph010" className="text-[--color-neutral-600]">
                  Scoring Starts*
                </Typography>
                <div className="bg-[#f3f3f3] border border-[#f3f3f3] rounded-[--corner-xs] px-[--padding-md] h-[48px] flex items-center justify-between">
                  <Typography variant="paragraph020" className="text-[--color-neutral-600]">
                    {periodLabel} {league.start_gameweek || 1}
                  </Typography>
                  <CaretDown className="size-5 text-[--color-neutral-600]" />
                </div>
                <Typography variant="smallprint010R" className="text-[--color-neutral-600]">
                  This will be when scoring starts for your league.
                </Typography>
              </fieldset>
            </div>

            <fieldset className="flex flex-col gap-[--padding-xs]">
              <Typography
                as="label"
                htmlFor="manage-league-description"
                variant="paragraph010"
                className="text-[--inputfield-label-color]"
              >
                League Description
              </Typography>
              <div className="bg-[--inputfield-input-bg-default] border border-transparent hover:border-[--inputfield-input-border-active] focus-within:border-[--inputfield-input-border-active] rounded-[--corner-xs] p-[--padding-md]">
                <textarea
                  id="manage-league-description"
                  rows={4}
                  placeholder="Who is the league for? Any additional rules..."
                  value={formDescription}
                  onChange={e => setFormDescription(e.target.value.slice(0, 100))}
                  className="w-full bg-transparent outline-none resize-none font-primary text-14 leading-20 text-[--color-neutral-900] placeholder:text-[--color-neutral-600]"
                />
              </div>
              <div className="flex justify-between">
                <Typography variant="smallprint010R" className="text-[--inputfield-label-color]">
                  Optional
                </Typography>
                <Typography variant="smallprint010R" className="text-[--inputfield-label-color]">
                  {formDescription.length}/100
                </Typography>
              </div>
            </fieldset>

            <Button
              variant="contained"
              color="blue"
              disabled={!isDirty || updating}
              loading={updating}
              onClick={handleUpdateSettings}
              className="self-start"
            >
              Update Settings
            </Button>
          </div>

          <div className="flex flex-col gap-[--padding-xs]">
            <Typography
              as="h2"
              variant="headline025"
              className="text-[--color-neutral-900] !font-[900]"
            >
              REMOVE PLAYERS
            </Typography>
            <div className="flex flex-col w-full">
              <LeaderBoardLabel showAverage={false} gameweekPointsLabel={pointsColumnLabel} />
              {entries.map(entry => {
                const isMyRow = entry.user_id === userId;
                const isRemoving = removingEntryId === entry.id;
                return (
                  <div
                    key={entry.id}
                    className={[
                      'flex items-center gap-2 min-h-[52px] px-2 py-3 border-b',
                      isMyRow
                        ? 'bg-[#201d4a] border-[#030033] border-t'
                        : 'bg-white border-[#eae9fd]',
                    ].join(' ')}
                  >
                    <div className="flex flex-1 min-w-0 items-center gap-2">
                      <Typography
                        variant="paragraph010"
                        className={`w-[50px] text-center shrink-0 ${isMyRow ? 'text-white' : 'text-[#222526]'}`}
                      >
                        {entry.rank ?? '-'}
                      </Typography>
                      <div className="flex flex-col min-w-0 flex-1">
                        <Typography
                          variant="paragraph010"
                          className={`truncate ${isMyRow ? 'text-white' : 'text-[#222526]'}`}
                        >
                          {entry.fantasy_team?.team_name ?? '-'}
                        </Typography>
                        {entry.user?.username && (
                          <Typography
                            variant="smallprint010R"
                            className={`truncate ${isMyRow ? 'text-white/70' : 'text-[#757777]'}`}
                          >
                            {entry.user.username}
                          </Typography>
                        )}
                      </div>
                    </div>
                    {!isMyRow && (
                      <button
                        type="button"
                        disabled={isRemoving}
                        onClick={() => setConfirmRemove(entry)}
                        className="shrink-0 px-3 py-2 text-[--color-blue-200] font-bold text-[14px] leading-[20px] disabled:opacity-50 hover:opacity-70 transition-opacity"
                      >
                        {isRemoving ? 'Removing...' : 'Remove'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {canRegenerate ? (
            <RegenerateLeagueBanner onRegenerate={() => setShowRegenerateModal(true)} />
          ) : (
            !isPreviousSeason && <InviteLeagueBanner league={league} />
          )}
        </div>
      )}

      {/* ── Details tab (non-chair members) ─────────────────────────────── */}
      {activeTab === 'details' && !isChair && league && (
        <div className="max-w-[846px] mx-auto w-full flex flex-col gap-[--padding-xl] px-4 py-[--padding-lg]">
          <div className="flex flex-col gap-[--padding-sm]">
            <Typography
              as="h2"
              variant="headline030"
              className="text-[--color-neutral-900] !font-[900]"
            >
              ABOUT THIS LEAGUE
            </Typography>
            {league.commissioner_username && (
              <div className="flex flex-col gap-1">
                <Typography variant="paragraph010" className="text-[--color-neutral-600]">
                  Chairman:
                </Typography>
                <Typography variant="subheadline020" className="text-[--color-neutral-900]">
                  {league.commissioner_username}
                </Typography>
              </div>
            )}
            {leagueDescription && (
              <Typography variant="paragraph020" className="text-[--color-neutral-900]">
                {leagueDescription}
              </Typography>
            )}
            {isMiniLeague && (
              <Typography variant="paragraph020" className="text-[--color-neutral-600]">
                If you wish to leave the league, you can rejoin if you wish to do so.
              </Typography>
            )}
          </div>

          {isMiniLeague && !isPreviousSeason && <InviteLeagueBanner league={league} />}

          {isMiniLeague && (
            <div className="flex flex-col gap-[--padding-lg]">
              <Typography
                as="h2"
                variant="headline030"
                className="text-[--color-neutral-900] !font-[900]"
              >
                WANT TO LEAVE THE LEAGUE?
              </Typography>
              <Button
                variant="outlined"
                color="blue"
                className="self-start"
                disabled={!!removingEntryId}
                onClick={async () => {
                  if (!myEntry) return;
                  try {
                    await removeEntry(leagueId, myEntry.id);
                    navigate('/leagues');
                  } catch (e) {
                    toast.error(
                      getErrorMessage(e, 'Could not leave the league. Please try again.')
                    );
                  }
                }}
              >
                {removingEntryId ? 'Leaving…' : 'Leave League'}
              </Button>
            </div>
          )}
        </div>
      )}
      <ContentAdSlot id="ad-boa-leaderboard" />
    </div>
  );
};

export default LeagueDetail;
